import { AppDate } from '@macs-frontend/core/utils/date';
import { BaseInvoice } from '@macs-frontend/features/shared/models/invoice.model';
import { BaseEntity } from '@macs-frontend/web-macs/shared/models/base-entity.model';
import { BaseClient } from '@macs-frontend/web-macs/shared/models/client';
import { BaseCompany } from '@macs-frontend/web-macs/shared/models/company.model';
import { Currency } from '@macs-frontend/web-macs/shared/models/currency.model';
import {
  BaseExpense,
  BaseExpenseGroup,
} from '@macs-frontend/web-macs/shared/models/expense';
import { InvoiceItem } from '@macs-frontend/web-macs/shared/models/invoice-item.model';
import { BaseProject } from '@macs-frontend/web-macs/shared/models/project';
import { BaseProjectGroup } from '@macs-frontend/web-macs/shared/models/project-group';
import { BaseSupplier, Supplier } from '@macs-frontend/web-macs/shared/models/supplier';
import { UploadedFile } from '@macs-frontend/web-macs/shared/models/uploaded-file';
import { BaseUser, User } from '@macs-frontend/web-macs/shared/models/user.model';
import { SpoPermission } from '@macs-gql';
import {
  DictionaryService,
  TeamCostTeamType,
} from '@macs-frontend/features/company-management/team-cost/team-cost.types';
import { Event } from '@macs-frontend/features/shared/models/event.model';

export interface BaseSPOGroup extends BaseEntity {
  name: string;
}

export interface BaseSPO extends BaseEntity {
  reqNumber: string;
  spoNumber: string;
  subject: string;
  issueDate?: AppDate;
  tax?: number;
  requester?: User;
  estimatedDeliveryDate?: AppDate;
  status: SPOStatuses;
  statusNote: string;
  totalAmount: number;
  notes?: string;
  permissions?: Record<SpoPermission, boolean>;
  statusNoteFlagged: boolean;
  project: BaseProject;
  supplier: BaseSupplier;
  reviewer: BaseUser;
  currency?: Currency;
  lastEvents?: SpoLastEvents | null;
  linkedEstimate: BaseInvoice | null;
}

export interface SPOSupplier
  extends BaseSupplier,
    Pick<Supplier, 'city' | 'country' | 'phones'> {}

export enum SPOItemTypes {
  Expense = 'EXPENSE',
  UnratedExpense = 'UNRATED_EXPENSE',
  Service = 'SERVICE',
  TeamCost = 'TEAM_COST',
}

export const SPOItemTypeTitles = {
  [SPOItemTypes.Expense]: 'Rated expense',
  [SPOItemTypes.UnratedExpense]: 'Unrated expense',
  [SPOItemTypes.Service]: 'Service',
  [SPOItemTypes.TeamCost]: 'Team cost',
};

export interface BaseSPOItem extends BaseEntity {
  name: string;
  type: SPOItemTypes | null;
  description: null | string;
  qty1: number;
  qty2: number;
  unitPrice: number;
  discount: null | number;
  billableAmount?: number;
  applyTax: boolean;
  margin: number;
}

export interface SPOItem extends BaseSPOItem {
  spoGroup: BaseSPOGroup;
  SPO: BaseSPO;
  expense: BaseExpense | null;
  teamType: TeamCostTeamType | null;
  service: DictionaryService | null;
  previousAmountForMargin: number;
  invoiceItem: Pick<
    InvoiceItem,
    | 'uuid'
    | 'name'
    | 'unitPrice'
    | 'billablePrice'
    | 'spoAmount'
    | 'spoQty1'
    | 'spoQty2'
    | 'qty1'
    | 'qty1Type'
    | 'qty2'
    | 'qty2Type'
    | 'discount'
    | 'amount'
    | 'quantity'
    | 'billablePriceMargin'
  > | null;
}

export interface SPOGroup extends BaseSPOGroup {
  expenseGroup: BaseExpenseGroup;
  items?: SPOItem[];
}

export enum SPOStatuses {
  DRAFT = 'DRAFT',
  IN_REVIEW = 'IN_REVIEW',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  ARCHIVED = 'ARCHIVED',
}

export enum SPOTabsStatuses {
  ALL = 'ALL',
}

export const SPOStatusLabels = {
  [SPOTabsStatuses.ALL]: 'All',
  [SPOStatuses.DRAFT]: 'Draft',
  [SPOStatuses.IN_REVIEW]: 'In Review',
  [SPOStatuses.APPROVED]: 'Approved',
  [SPOStatuses.REJECTED]: 'Rejected',
  [SPOStatuses.ARCHIVED]: 'Archived',
};

export interface SPO extends BaseSPO {
  client: BaseClient;
  supplier: SPOSupplier;
  projectGroup: BaseProjectGroup;
  invoiceItem?: InvoiceItem;
  items: SPOItem[];
  files: UploadedFile[];
  company?: BaseCompany;
  spoGroups?: SPOGroup[];
  subtotal?: number;
  filterExpensesByProject: boolean;
  statusNoteEditor: BaseUser;
  preferredSupplier: boolean;
  billableSubtotal: number;
}

interface SpoLastEvents {
  created?: Event | null;
  edited?: Event | null;
  approved?: Event | null;
  sentForReview?: Event | null;
}
